import { BLOCK, CLIENT_EVENTS, AWAIT, LOCAL_ACTIONS } from "./agentContract";
import userDetail from "Stores/userDetail";
import { settingsStoreData } from "Stores/settingsData";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import { getDomainFromEmail, isFreeMailDomain } from "Utils/emailDomain";
import { MXP_EVENT } from "Static/MixPanelEvents";
import * as svc from "./onboardingServices";

/**
 * The new-user copilot onboarding: a deterministic 10-step flow that turns a
 * signup into an activate-ready campaign, all inside the copilot drawer. Each
 * step is { id, expects(clientEvent, ctx) -> bool, run(api, clientEvent, ctx) }
 * driven by scriptedTransportBase; run() awaits the real service calls in
 * onboardingServices.js. ctx is onboardingCopilotStore.
 *
 * Copy rules (spec): no em-dashes, no exclamation-mark enthusiasm, numbers
 * over adjectives. Loaders are 4-5 rotating messages, never a bare spinner.
 * Onboarding never sends; activation stays on the sequence detail page.
 */

const firstName = () => userDetail?.userInfo?.firstName || "there";

export const signupDomain = () => {
  const brand = userDetail?.brandUrlInfo?.domain || "";
  if (brand) return brand;
  const email =
    settingsStoreData?.userProfileData?.email || userDetail?.userInfo?.email || "";
  return getDomainFromEmail(email);
};

const track = (event, props = {}) => {
  try {
    if (window && window.mixpanel) window.mixpanel.track(event, props);
  } catch (e) {
    /* instrumentation is best-effort */
  }
};

const isCta = (evt, actionId) =>
  evt.type === CLIENT_EVENTS.CTA_CLICKED && evt.actionId === actionId;
const isReply = (evt, replyId) =>
  evt.type === CLIENT_EVENTS.QUICK_REPLY_SELECTED && evt.replyId === replyId;

// A 4-5 message rotating loader that runs `work` (a promise) to completion,
// then flips the block to done. Returns whatever `work` resolved to.
const runWork = async (api, turn, { label, messages, detail }, work) => {
  const blockId = turn.openBlock(BLOCK.PROGRESS, { status: "running", label, messages });
  try {
    const result = await work;
    turn.patch(blockId, { status: "done", detail });
    turn.closeBlock(blockId);
    return result;
  } catch (err) {
    turn.patch(blockId, { status: "failed", detail: "That step could not finish." });
    turn.closeBlock(blockId);
    throw err;
  }
};

// Names the step a returning user left on, for the resume prompt.
const STEP_LABELS = {
  1: "what you sell",
  2: "your seller profile",
  3: "who to target",
  4: "your first search",
  5: "your list",
  6: "enrichment",
  7: "connecting your inbox",
  8: "your first campaign"
};

const LEVEL_LABEL = { cxo: "C-suite", vp: "VP", director: "Director", manager: "Manager", senior: "Senior" };

// One readable line for the ICP: "{levels} in {departments} at {industries}
// companies, {sizes} employees, {geo}". Empty slots drop out; never a filter
// dump. Falls back to a neutral phrase when nothing is known.
const titleCase = s => String(s).replace(/\b[a-z]/g, c => c.toUpperCase()).replace(/\b(Of|And|For)\b/g, m => m.toLowerCase());

// "51-200, 201-1000, 1001-10000" -> "51 to 10,000"
const sizeSpan = buckets => {
  const nums = (buckets || []).flatMap(b => String(b).split("-").map(n => parseInt(n, 10))).filter(n => n > 0);
  if (!nums.length) return "";
  return `${Math.min(...nums).toLocaleString()} to ${Math.max(...nums).toLocaleString()}`;
};

const icpSummary = icp => {
  const i = icp || {};
  const list = (arr, n) => (arr || []).filter(Boolean).slice(0, n).join(", ");
  const titles = (i.title || []).filter(Boolean);
  const who = titles.length
    ? list(titles.map(titleCase), 3) + (titles.length > 3 ? ` and ${titles.length - 3} more` : "")
    : list((i.level || []).map(l => LEVEL_LABEL[l] || l), 3);
  const industries = list(i.industry, 2);
  const sizes = sizeSpan(i.employeeCount);
  const geo = list(i.country, 2);
  const where = [
    industries && `at ${industries} companies`,
    sizes && `${sizes} employees`,
    geo && `in ${geo}`
  ].filter(Boolean);
  const out = [who, where.join(", ")].filter(Boolean).join(" ");
  return out || "people who match your profile";
};

// ── Step 0: welcome (emitted on connect, no client event) ────────────────
export const emitWelcome = async api => {
  const turn = api.beginTurn();
  onboardingCopilotStore.begin("onboarding", userDetail?.userInfo?.memberId);
  onboardingCopilotStore.setStep(0);
  track(MXP_EVENT.ONBOARDING_STARTED);
  await turn.text(
    `Hey ${firstName()}, welcome to Salesgear. I'm your Salesgear copilot. In the next couple of minutes I'll find your buyers, research them, and get your first campaign ready for you to review.`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_get_started", label: "Let's go", icon: "arrow_forward", style: "primary" },
      { actionId: "ob_dismiss", label: "I'll explore on my own" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// ── Step 2-3: research the seller, save, capture ICP ─────────────────────
async function runProfileStep(api, ctx, website) {
  const turn = api.beginTurn();
  ctx.setStep(2);
  const domain = website || signupDomain();
  let draft;
  try {
    draft = await runWork(
      api,
      turn,
      {
        label: "Building your seller profile",
        messages: [
          `Reading ${domain}...`,
          "Understanding what you sell...",
          "Mapping your market...",
          "Identifying who buys this...",
          "Checking who you compete with..."
        ],
        detail: "Seller profile drafted"
      },
      svc.enrichSeller(website)
    );
  } catch (err) {
    await turn.text(
      `I couldn't build a profile from ${domain}. Let's build it together in the profile builder, same two minutes. I'll pick up from your search when you're back.`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        {
          actionId: LOCAL_ACTIONS.NAVIGATE,
          path: "/settings/seller-profiles",
          label: "Open profile builder",
          style: "primary"
        }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  ctx.patchArtifacts({ draftProfile: draft });
  await turn.text(`Here's what I understood from ${domain}. Edit any card, or just tell me what's off.`);
  turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: draft, done: false });
  turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
}

// The adjust form. Options matching the derived ICP arrive pre-ticked so the
// user is editing what the copilot already knows, not filling a blank survey.
const ICP_QUESTIONS = derived => {
  const d = derived || {};
  const has = (arr, v) => (arr || []).includes(v);
  const regionOn = label => (REGION_MAP[label] || []).some(c => has(d.country, c));
  return [
    {
      question: "Which seniority levels should I target?",
      header: "Seniority",
      multiSelect: true,
      options: Object.keys(LEVEL_MAP).map(label => ({ label, selected: has(d.level, LEVEL_MAP[label]) }))
    },
    {
      question: "What company sizes fit best?",
      header: "Company size",
      multiSelect: true,
      options: Object.keys(SIZE_MAP).map(label => ({ label, selected: has(d.employeeCount, SIZE_MAP[label]) }))
    },
    {
      question: "Which regions should I search?",
      header: "Geography",
      multiSelect: true,
      options: Object.keys(REGION_MAP).map(label => ({ label, selected: regionOn(label) }))
    }
  ];
};

const LEVEL_MAP = { "C-Suite": "cxo", VP: "vp", Director: "director", Manager: "manager" };
const SIZE_MAP = {
  "1-50": "1-50",
  "51-200": "51-200",
  "201-1000": "201-1000",
  "1000+": "1001-10000"
};
const REGION_MAP = {
  "North America": ["United States", "Canada"],
  Europe: ["United Kingdom", "Germany", "France"],
  "Asia Pacific": ["India", "Australia", "Singapore"],
  Global: []
};

const answersToIcp = (answers, derived) => {
  const pick = header =>
    (answers.find(a => (a.question || "").toLowerCase().includes(header)) || {}).answer || "";
  const list = v => (Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean));
  const levels = list(pick("seniority")).map(l => LEVEL_MAP[l]).filter(Boolean);
  const sizes = list(pick("size")).map(s => SIZE_MAP[s]).filter(Boolean);
  const regions = list(pick("region")).flatMap(r => REGION_MAP[r] || []);
  return {
    title: derived.title || [],
    level: levels.length ? levels : derived.level || [],
    industry: derived.industry || [],
    employeeCount: sizes.length ? sizes : derived.employeeCount || [],
    country: regions.length ? regions : derived.country || []
  };
};

// Step 1: what are you selling. Shared by the Get-started path and by resume,
// which drops a user back here rather than replaying the Step 0 welcome.
export const emitSellingQuestion = async (api, ctx) => {
  ctx.setStep(1);
  const domain = signupDomain();
  const turn = api.beginTurn();
  if (domain && !isFreeMailDomain(domain)) {
    await turn.text(
      `First, what you sell. Your email says you're at **${domain}**. Should I read that, or is there a specific product page I should use instead?`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: "ob_use_domain", label: `Use ${domain}`, style: "primary" },
        { actionId: "ob_specific_product", label: "It's a specific product" }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  await turn.text(
    "First, what you sell. Paste a link to your company site, or to the product page that describes it best."
  );
  turn.addBlock(BLOCK.WEBSITE_INPUT, {});
  turn.end(AWAIT.WEBSITE_INPUT);
};

// Resume: never restart from Step 0. Offer to pick up, then jump to the right
// point from the persisted artifacts.
export const emitResume = async api => {
  const turn = api.beginTurn();
  const where = STEP_LABELS[onboardingCopilotStore.step] || "setup";
  await turn.text(
    `Welcome back, ${firstName()}. You were on ${where}. Pick up where we left off?`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_resume", label: "Continue", style: "primary" },
      { actionId: "ob_restart", label: "Start over (keeps what's saved)" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// Step 3: who to reach. Shared by resume and by the off-script re-emit, which
// must return the user to the ICP form and not to the already-approved profile.
export const emitIcpForm = async (api, ctx) => {
  const a = ctx.artifacts || {};
  const derived = a.icpFilters || ctx._icpDerived || (await svc.deriveIcp(a.draftProfile || {}));
  ctx._icpDerived = derived;
  const turn = api.beginTurn();
  await turn.text(
    svc.hasIcp(derived)
      ? "Adjust who to reach. I've ticked what I got from your profile. Change anything, then I'll search."
      : "I couldn't pin down who to reach from your site. Tell me, then I'll search."
  );
  turn.addBlock(BLOCK.QUESTION_FORM, { questions: ICP_QUESTIONS(derived) });
  turn.end(AWAIT.QUESTION_FORM);
};

// Step 3.5: the ICP as a one-line confirmation, not a questionnaire. The
// copilot states who it will target (from the seller intelligence) and asks
// to proceed; the form is only the adjust path.
export const emitIcpConfirm = async (api, ctx, turn) => {
  ctx.setStep(3);
  const icp = (ctx.artifacts && ctx.artifacts.icpFilters) || ctx._icpDerived;
  if (!svc.hasIcp(icp)) return emitIcpForm(api, ctx);
  turn = turn || api.beginTurn();
  await turn.text(
    `Here's who I'll target, based on your seller profile: **${icpSummary(icp)}**. Want me to find them?`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_icp_go", label: "Yes, find them", icon: "search", style: "primary" },
      { actionId: "ob_icp_adjust", label: "Adjust who to target" }
    ]
  });
  turn.end(AWAIT.CTA);
  return undefined;
};

async function resumeFromArtifacts(api, ctx) {
  const a = ctx.artifacts || {};
  if (a.sequenceId) {
    const turn = api.beginTurn();
    await turn.text("Your campaign is already drafted. Open it to review and activate.");
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: `/sequences/all/${a.sequenceId}`, label: "Review and activate", style: "primary" }
      ]
    });
    turn.end(AWAIT.CTA);
    ctx.complete();
    return;
  }
  if (a.listId) {
    return emitConnectStep(api, ctx);
  }
  if (a.savedProfileId || a.draftProfile) {
    return a.icpFilters ? emitIcpConfirm(api, ctx) : emitIcpForm(api, ctx);
  }
  // Past the welcome but nothing captured yet (the commonest drop-off point).
  // Resume at the question, never by replaying Step 0.
  return emitSellingQuestion(api, ctx);
}

// ── the scenario steps ────────────────────────────────────────────────────
// Steps 0-4 (entry through the first search); steps 5-9 are in laterSteps().
const earlySteps = () => [
  {
    id: "resume_pick",
    expects: evt => isCta(evt, "ob_resume"),
    run: async (api, evt, ctx) => resumeFromArtifacts(api, ctx)
  },
  {
    id: "resume_restart",
    expects: evt => isCta(evt, "ob_restart"),
    run: async (api, evt, ctx) => {
      ctx.status = "idle";
      ctx.begin("onboarding", userDetail?.userInfo?.memberId);
      return emitWelcome(api);
    }
  },
  // Step 1: entry / dismissal
  {
    id: "start_or_dismiss",
    expects: evt => isCta(evt, "ob_get_started") || isCta(evt, "ob_dismiss"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_dismiss") {
        ctx.dismiss();
        track(MXP_EVENT.ONBOARDING_DISMISSED, { at: "welcome" });
        const turn = api.beginTurn();
        await turn.text(
          "No problem. I'll be here in the copilot whenever you want to set things up."
        );
        turn.end(AWAIT.USER_INPUT);
        return;
      }
      return emitSellingQuestion(api, ctx);
    }
  },

  {
    id: "choose_product_source",
    expects: evt => isCta(evt, "ob_use_domain") || isCta(evt, "ob_specific_product"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_use_domain") {
        const domain = signupDomain();
        ctx.patchArtifacts({ website: domain });
        track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "domain" });
        return runProfileStep(api, ctx, domain);
      }
      const turn = api.beginTurn();
      await turn.text("Paste the link that best describes what you sell.");
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
    }
  },

  {
    id: "website_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.WEBSITE_SUBMITTED,
    run: async (api, evt, ctx) => {
      const website = (evt.website || "").trim();
      ctx.patchArtifacts({ website });
      track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "link" });
      return runProfileStep(api, ctx, website);
    }
  },

  // Step 3: profile approved -> save + intelligence + ICP capture
  {
    id: "profile_approved",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_APPROVED,
    run: async (api, evt, ctx) => {
      const profile = evt.profile || ctx.artifacts.draftProfile || {};
      const turn = api.beginTurn();
      ctx.setStep(3);
      await turn.text(
        "Saving your profile. Now I'm building your seller intelligence, the research layer I use to personalise every message you send."
      );
      // Save, then generate the seller intelligence (personas, ICP,
      // competitors) in the same loader. The ICP shown next is read off that
      // intelligence; the client heuristic is only the fallback.
      const { saved, intel } = await runWork(
        api,
        turn,
        {
          label: "Building your seller intelligence",
          messages: [
            "Saving your profile...",
            "Indexing your value props...",
            "Mapping who buys this...",
            "Preparing your ICP..."
          ],
          detail: "Seller intelligence ready. Your profile lives in Settings, edit it any time."
        },
        (async () => {
          const s = await svc.saveSeller(profile);
          const i = s && s.id ? await svc.deriveSellerIntelligence(s.id).catch(() => null) : null;
          return { saved: s, intel: i };
        })()
      ).catch(() => ({ saved: null, intel: null }));
      ctx.patchArtifacts({ savedProfileId: (saved && saved.id) || "" });
      track(MXP_EVENT.ONBOARDING_PROFILE_CONFIRMED, { intelligence: !!intel });
      const icp = intel ? svc.icpFromIntelligence(intel, profile) : await svc.deriveIcp(profile);
      ctx._icpDerived = icp;
      ctx.patchArtifacts({ icpFilters: icp });
      return emitIcpConfirm(api, ctx, turn);
    }
  },

  // Step 3.5: confirm or adjust the derived ICP
  {
    id: "icp_go",
    expects: evt => isCta(evt, "ob_icp_go"),
    run: async (api, evt, ctx) => runSearchStep(api, ctx, ctx.artifacts.icpFilters || ctx._icpDerived || {})
  },
  {
    id: "icp_adjust",
    expects: evt => isCta(evt, "ob_icp_adjust"),
    run: async (api, evt, ctx) => emitIcpForm(api, ctx)
  },

  {
    id: "profile_discarded",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_DISCARDED,
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "No problem. Paste a different link and I'll research it again, or build the profile yourself in Settings."
      );
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
      return false; // allow another website submission to re-enter
    }
  },

  // Step 4-5: search + save list
  {
    id: "icp_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.QUESTION_FORM_RESPONSE,
    run: async (api, evt, ctx) => {
      const derived = ctx._icpDerived || ctx.artifacts.icpFilters || { industry: [], country: [] };
      const icp = answersToIcp(evt.answers || [], derived);
      return runSearchStep(api, ctx, icp);
    }
  }
];

// Step 4: the first search. Shared by the ICP confirmation ("Yes, find them")
// and the adjust form, so both paths run the identical search + save prompt.
async function runSearchStep(api, ctx, icp) {
      ctx.patchArtifacts({ icpFilters: icp });
      ctx.setStep(4);
      const turn = api.beginTurn();
      await turn.text(`Now your buyers. Searching for ${icpSummary(icp)}.`);
      let result = await runWork(
        api,
        turn,
        {
          label: "Searching your ICP",
          messages: [
            "Matching titles and seniority...",
            "Filtering by company size...",
            "Checking regions...",
            "Ranking by fit..."
          ],
          detail: "Search done"
        },
        svc.runSearch(icp)
      ).catch(() => ({ total: 0, rows: [] }));
      if ((result.total || 0) < 10) {
        const narrow = result.total || 0;
        const loosened = await svc.loosenSearch(icp).catch(() => null);
        if (loosened && loosened.dropped) {
          result = loosened;
          await turn.text(
            `Only ${narrow} matched, so I widened the ${loosened.dropped} filter and found ${(result.total || 0).toLocaleString()}. You can narrow it back any time.`
          );
        }
      }
      ctx.patchArtifacts({ searchTotal: result.total || 0 });
      track(MXP_EVENT.ONBOARDING_SEARCH_DONE, { total: result.total || 0 });
      // Nothing matched, even after loosening. An empty table and an offer to
      // "save these as your first list" is nonsense with zero rows, so end the
      // step on the two things that can actually move it forward.
      if (!(result.total || 0)) {
        await turn.text(
          "Nothing matched that profile, even after widening it. The targeting is usually too narrow when this happens, so let's change who we look for or refine it in the list builder."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            { actionId: "ob_icp_adjust", label: "Adjust who to target", style: "primary" },
            { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/list-builder", label: "Refine in the list builder" }
          ]
        });
        turn.end(AWAIT.CTA);
        return undefined;
      }
      const shown = Math.min((result.rows || []).length, 25);
      await turn.text(
        `**${(result.total || 0).toLocaleString()}** people match your ICP. Here are the first ${shown}.`
      );
      // The standard prospect table (opens the shared side panel as a popup in
      // the drawer). Served by the transport's fetchTablePage from the loaded
      // results, so it looks and behaves like the discover search.
      turn.addBlock(BLOCK.TABLE_REF, {
        tableId: "ob_prospects",
        title: "People matching your ICP",
        totalCount: shown,
        selectable: true,
        selectionScope: "page",
        // ContactActionBar denies every action unless the table names it, so
        // selection alone rendered a bar with nothing to do. Prospect rows are
        // not owned yet, so add-to-list is the only one that applies.
        actions: ["add_to_list"],
        columns: [
          { key: "name", label: "Name" },
          { key: "title", label: "Title" },
          { key: "company", label: "Company" },
          { key: "industry", label: "Industry" },
          { key: "location", label: "Location" }
        ]
      });
      // The TABLE_REF card above already carries its own "View", so no separate
      // "View the list" chip here (it was a duplicate control).
      turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Want me to save these as your first list?",
        replyId: "save_list",
        options: [
          { value: "save", label: "Add to list" },
          { value: "refine", label: "Refine the search first" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
}

// Steps 5-9 continue the scenario list started above.
const laterSteps = () => [
  {
    id: "save_or_refine",
    expects: evt => isReply(evt, "save_list"),
    run: async (api, evt, ctx) => {
      if (evt.value === "refine") {
        // Refine in the list builder, then continue: spell out exactly how to
        // save the refined selection so the flow can pick up from the list.
        const turn = api.beginTurn();
        await turn.text(
          "Open the list builder to refine titles and filters. When the results look right:\n" +
            "1. Tick the contacts you want (or Select all).\n" +
            "2. Click **Add to list** and pick or name a list.\n" +
            "3. Come back here and I'll continue from that saved list."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            {
              actionId: LOCAL_ACTIONS.NAVIGATE,
              path: "/list-builder",
              label: "Open list builder",
              style: "primary"
            }
          ]
        });
        turn.end(AWAIT.CTA);
        return false; // stay available so a later save can proceed
      }
      ctx.setStep(5);
      const turn = api.beginTurn();
      const company = userDetail?.userInfo?.companyName || ctx.artifacts.website || "";
      const listName = company ? `${company} ICP prospects` : "ICP prospects";
      const saved = await runWork(
        api,
        turn,
        {
          label: "Saving your list",
          messages: ["Creating your list...", "Adding matched prospects...", "Finishing up..."],
          detail: "List saved"
        },
        svc.saveList(listName, null, ctx.artifacts.searchTotal)
      ).catch(() => null);
      const listSize = (saved && saved.listSize) || 0;
      const finalName = (saved && saved.listName) || listName;
      ctx.patchArtifacts({
        listId: (saved && saved.listId) || "",
        listName: finalName,
        listSize
      });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED);
      if (saved) {
        await turn.text(
          `Saved as **${finalName}**.${listSize ? ` ${listSize.toLocaleString()} contacts.` : ""}`
        );
      }
      return emitEnrichStep(api, ctx, turn);
    }
  },

  // The user hand-picked prospects in the popup table and clicked "Add to
  // list". Add exactly those, then continue the flow into enrich, the same as
  // the scripted "Add to list" quick reply would.
  {
    id: "prospects_added_via_panel",
    expects: evt => evt.type === CLIENT_EVENTS.ADD_PROSPECTS_TO_LIST,
    run: async (api, evt, ctx) => {
      ctx.setStep(5);
      const rows = evt.rows || [];
      const count = rows.length;
      // Close the popup right away, then show a loader in the copilot while the
      // add runs -- rather than freezing the open popup with no feedback.
      closeTablePopup();
      const turn = api.beginTurn();
      const result = await runWork(
        api,
        turn,
        {
          label: `Adding ${count.toLocaleString()} ${count === 1 ? "contact" : "contacts"} to your list`,
          messages: ["Saving your selection...", "Updating your list...", "Finishing up..."],
          detail: "Contacts added"
        },
        svc.addRowsToList(evt.listId, rows)
      ).catch(() => ({ added: 0, skipped: count }));
      // Patch the table block so the shipped toast reports the real numbers.
      if (evt.blockId) {
        turn.patch(evt.blockId, {
          listResult: { added: result.added || 0, skipped: result.skipped || 0, listId: evt.listId }
        });
      }
      ctx.patchArtifacts({
        listId: evt.listId || "",
        listName: svc.getListName(evt.listId) || ctx.artifacts.listName || "",
        listSize: result.added || count
      });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "panel" });
      await turn.text(
        `Added ${(result.added || count).toLocaleString()} contacts to your list. Let's keep going.`
      );
      return emitEnrichStep(api, ctx, turn);
    }
  },

  // Step 6: enrich choice
  {
    id: "enrich_choice",
    expects: evt =>
      isCta(evt, "ob_enrich_email") || isCta(evt, "ob_enrich_both") || isCta(evt, "ob_enrich_skip"),
    run: async (api, evt, ctx) => {
      ctx.setStep(6);
      const turn = api.beginTurn();
      if (evt.actionId !== "ob_enrich_skip") {
        const type = evt.actionId === "ob_enrich_both" ? "EMAIL_PHONE" : "EMAIL";
        ctx.patchArtifacts({ enrichChoice: type });
        // Enrichment is a background bulk job (it can take a while for a large
        // list) and reports its own progress/errors via the app's popups and
        // toasts. Kick it off WITHOUT blocking the turn on it — otherwise the
        // copilot loader hangs for the whole job and Stop can't cancel an
        // awaited network call. Start it, tell the user, and move on.
        svc.enrichContacts(type, ctx.artifacts.listId).catch(() => {});
        track(MXP_EVENT.ONBOARDING_ENRICHED, { type });
        await turn.text(
          "Enrichment is running in the background. Anyone without a verified email stays out of the campaign, and you can keep going here while it finishes."
        );
      } else {
        await turn.text("No problem. I'll build the campaign with the contacts that already have an email.");
      }
      return emitConnectStep(api, ctx);
    }
  },

  // Step 7: connect (loops on re-check, stays available)
  {
    id: "connect_check",
    expects: evt => isCta(evt, "ob_check_connection"),
    run: async (api, evt, ctx) => {
      const status = svc.connectStatus();
      ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
      if (status.email) {
        track(MXP_EVENT.ONBOARDING_INBOX_CONNECTED);
        return emitCampaignStep(api, ctx);
      }
      const turn = api.beginTurn();
      await turn.text(
        "I don't see a connected inbox yet. Connect your email in Settings, then choose Check connection."
      );
      turn.end(AWAIT.CTA);
      return false;
    }
  },

  {
    id: "connect_later",
    expects: evt => isCta(evt, "ob_connect_later"),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "No problem. Your profile and list are saved here. Campaigns need a connected inbox, so when yours is connected, reopen the copilot and choose Check connection."
      );
      turn.end(AWAIT.CTA);
    }
  },

  // Step 8: campaign style choice. The style cards themselves are the buttons
  // (COMPARISON_GRID cta), so the choice arrives as a user message.
  {
    id: "campaign_choice",
    expects: evt =>
      evt.type === CLIENT_EVENTS.USER_MESSAGE &&
      /create the .*campaign|connect linkedin to continue/i.test(evt.text || ""),
    run: async (api, evt, ctx) => {
      const text = (evt.text || "").toLowerCase();
      if (/connect linkedin/.test(text)) {
        const turn = api.beginTurn();
        await turn.text(
          "LinkedIn connects through the Salesgear Chrome extension. Connect it in Settings, then pick a LinkedIn style here."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            {
              actionId: LOCAL_ACTIONS.NAVIGATE,
              path: "/settings/account",
              label: "Connect LinkedIn",
              style: "primary"
            }
          ]
        });
        turn.end(AWAIT.USER_INPUT);
        return false; // stay available so a style can still be picked
      }
      const status = svc.connectStatus();
      if (!status.email) {
        const turn = api.beginTurn();
        await turn.text("Connect your email inbox first, then I can build the campaign.");
        turn.end(AWAIT.USER_INPUT);
        return false;
      }
      ctx.setStep(9);
      const deep = /deep research/.test(text);
      const linkedIn = /linkedin/.test(text);
      // Onboarding does not write the steps itself. Hand off to the shipped
      // "build a sequence with Copilot" flow, which reads the saved seller
      // profile, creates the sequence with steps, shows them, and offers open /
      // add-contacts against the saved list. Contacts still require a connected
      // inbox (Step 7 gate) before that flow can enrol them.
      const style = deep
        ? "a deep research email and LinkedIn sequence"
        : linkedIn
          ? "an email and LinkedIn sequence"
          : "a 3-step email sequence";
      track(MXP_EVENT.ONBOARDING_CAMPAIGN_CREATED, { deep, linkedIn });
      const turn = api.beginTurn();
      await turn.text(
        `Good choice. I'll build ${style} from your seller profile now. Review the steps, then add your saved list. Nothing goes out until you activate it.`
      );
      turn.end(AWAIT.USER_INPUT);
      ctx.complete();
      // Swap to the live copilot's build-sequence flow after this turn settles.
      handoffToSequenceBuilder(`${sequenceBrief(ctx)}Build ${style} from my seller profile`);
    }
  }
];

const buildOnboardingScenario = () => [...earlySteps(), ...laterSteps()];

// Everything onboarding already asked the user, restated for the live
// sequence builder. Without it the build flow re-asked sequence type, audience
// and goal, and never learned about the list saved at Step 5.
function sequenceBrief(ctx) {
  const a = ctx.artifacts || {};
  const icp = a.icpFilters || {};
  const p = a.draftProfile || {};
  const bits = [];
  const join = v => (Array.isArray(v) ? v.filter(Boolean).join(", ") : "");
  const levels = join(icp.level);
  const sizes = join(icp.employeeCount);
  const regions = join(icp.country);
  const audience = [levels && `${levels} level`, sizes && `at ${sizes} employee companies`, regions && `in ${regions}`]
    .filter(Boolean)
    .join(" ");
  if (audience) bits.push(`Audience: ${audience}.`);
  const goal = String(p.goal_of_outreach || "").trim().replace(/[.\s]+$/, "");
  if (goal) bits.push(`Goal: ${goal}.`);
  if (a.listName) bits.push(`Enrol contacts from my list "${a.listName}".`);
  else if (a.listId) bits.push("Enrol the contacts from the list I just saved.");
  // Pin the seller profile just created in this session, so the build uses IT
  // (e.g. the Jenkins profile) rather than picking an older workspace profile.
  if (a.savedProfileId) {
    bits.push(
      `Use the seller profile I just created (seller_profile_id ${a.savedProfileId}); do not use any other profile.`
    );
  }
  if (!bits.length) return "";
  return `I already answered these during setup, so use them and skip the setup questions. ${bits.join(" ")} `;
}

// Closes the shared prospect table popup (lazy require avoids a circular
// import: copilotStore -> AgentTransport -> OnboardingAgentTransport -> here).
function closeTablePopup() {
  try {
    // eslint-disable-next-line global-require
    require("Stores/copilotStore").default.closeTablePanel();
  } catch (e) {
    /* panel may already be closed */
  }
}

// Hands the campaign build to the live copilot's shipped build-sequence flow.
// Deferred a tick so the onboarding turn finishes rendering before the
// transport is swapped underneath it. Lazy require avoids the circular import.
function handoffToSequenceBuilder(triggerText) {
  setTimeout(() => {
    try {
      // eslint-disable-next-line global-require
      require("Stores/copilotStore").default.handoffToLiveSequenceBuilder(triggerText);
    } catch (e) {
      /* best-effort: the user can still open the sequence builder manually */
    }
  }, 400);
}

// Step 6 emitter (enrich, cost shown before any deduction). Reused by the
// scripted save and by the popup add-to-list path so both continue the same
// way. Writes into the turn it is given.
async function emitEnrichStep(api, ctx, turn) {
  turn = turn || api.beginTurn();
  // The popup add-to-list path knows the id but not the name, and the old
  // "your list" fallback rendered as "Your list your list is set up".
  const listName = ctx.artifacts.listName || "";
  const listSize = ctx.artifacts.listSize || 0;
  const credits = await svc.getCredits();
  const remaining = credits.remaining || 0;
  const costPhrase = listSize
    ? `Enriching ${listSize.toLocaleString()} contacts by email costs about ${listSize.toLocaleString()} of your ${remaining.toLocaleString()} credits.`
    : `You have ${remaining.toLocaleString()} credits.`;
  await turn.text(
    `Before we write to ${listName ? `**${listName}**` : "them"}, let's get verified emails and phone numbers. Emails are 1 credit each, phone numbers 10. ${costPhrase}`
  );
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_enrich_email", label: "Enrich emails", style: "primary" },
      { actionId: "ob_enrich_both", label: "Emails and phones" },
      { actionId: "ob_enrich_skip", label: "Skip for now" }
    ]
  });
  turn.end(AWAIT.CTA);
}

// Step 7 emitter, shared by the enrich step and resume.
function emitConnectStep(api, ctx) {
  ctx.setStep(7);
  const status = svc.connectStatus();
  ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
  if (status.email) {
    return emitCampaignStep(api, ctx);
  }
  const turn = api.beginTurn();
  return turn
    .text(
      "Almost there. Connect your email so I can send from your inbox and write in your voice. LinkedIn is optional, it unlocks LinkedIn steps in your campaigns."
    )
    .then(() => {
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect email", style: "primary" },
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect LinkedIn" },
          { actionId: "ob_check_connection", label: "Check connection" },
          { actionId: "ob_connect_later", label: "I'll do this later" }
        ]
      });
      turn.end(AWAIT.CTA);
    });
}

// Step 8 emitter.
function emitCampaignStep(api, ctx) {
  ctx.setStep(8);
  const status = svc.connectStatus();
  const li = status.linkedIn;
  const turn = api.beginTurn();
  const listSize = (ctx.artifacts && ctx.artifacts.listSize) || 0;
  const who = listSize ? `these ${listSize.toLocaleString()} contacts` : "your contacts";
  return turn.text(`Your inbox is connected. How do you want to reach ${who}?`).then(() => {
    // Each card carries its own button (ctaLabel + prompt), so the card is the
    // action. No separate chip row. LinkedIn styles stay visible but gated.
    // Subtitles describe the style, not a promise that steps are pre-written:
    // Step 8 hands off to the build-a-sequence flow, which writes the steps
    // from the seller profile, so the cards describe written steps again.
    turn.addBlock(BLOCK.COMPARISON_GRID, {
      title: "Pick a style",
      options: [
        {
          title: "Email",
          subtitle: "3 email steps, written from your seller profile",
          ctaLabel: "Use email",
          prompt: "Create the email campaign"
        },
        {
          title: "Email + LinkedIn",
          subtitle: li ? "3 email steps plus a profile view and a connection request" : "Connect LinkedIn to use this",
          ctaLabel: li ? "Use email and LinkedIn" : "Connect LinkedIn to use this",
          prompt: li ? "Create the email and LinkedIn campaign" : "Connect LinkedIn to continue"
        },
        {
          title: "Deep Research: Email + LinkedIn",
          subtitle: "I research each contact first, then write every step for that person",
          recommended: true,
          ctaLabel: li ? "Use deep research" : "Connect LinkedIn to use this",
          prompt: li ? "Create the deep research campaign" : "Connect LinkedIn to continue"
        }
      ]
    });
    turn.end(AWAIT.USER_INPUT);
  });
}

// Re-shows the current step's prompt after an off-script interjection, so the
// flow returns to the rail. Re-derives from ctx (step + artifacts); never
// re-runs a mutating service call (steps 2/3 re-show the cached draft, not a
// fresh enrich).
export const emitCurrentStep = async (api, ctx) => {
  const step = ctx.step || 0;
  const a = ctx.artifacts || {};
  if (step <= 0) {
    return ctx.status === "active" && step > 0 ? emitResume(api) : emitWelcome(api);
  }
  if (step === 1) return emitSellingQuestion(api, ctx);
  if (step === 2) {
    // Only offer the draft again while it is genuinely unapproved. draftProfile
    // survives approval, so keying on it alone re-opened Approve/Discard for a
    // profile already saved.
    if (a.draftProfile && !a.savedProfileId) {
      const turn = api.beginTurn();
      await turn.text("Here's your seller profile. Edit any card, or continue.");
      turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: a.draftProfile, done: false });
      turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
      return undefined;
    }
    if (a.savedProfileId || a.draftProfile) {
      return a.icpFilters ? emitIcpConfirm(api, ctx) : emitIcpForm(api, ctx);
    }
    return emitSellingQuestion(api, ctx);
  }
  if (step === 3) {
    if (a.savedProfileId || a.draftProfile) {
      return a.icpFilters ? emitIcpConfirm(api, ctx) : emitIcpForm(api, ctx);
    }
    return emitSellingQuestion(api, ctx);
  }
  if (step === 4 || step === 5) {
    const turn = api.beginTurn();
    turn.addBlock(BLOCK.QUICK_REPLIES, {
      prompt: "Want me to save these as your first list?",
      replyId: "save_list",
      options: [
        { value: "save", label: "Add to list" },
        { value: "refine", label: "Refine the search first" }
      ]
    });
    turn.end(AWAIT.QUICK_REPLY);
    return undefined;
  }
  if (step === 6) return emitEnrichStep(api, ctx);
  if (step === 7) return emitConnectStep(api, ctx);
  if (step === 8) return emitCampaignStep(api, ctx);
  return undefined; // step 9 / complete: nothing to re-emit
};

export default buildOnboardingScenario;
